import { useCallback, useEffect, useState } from 'react';
import { BookOpen, Clock, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { FlowHistoryPanel } from '@/components/creationFlows/FlowHistoryPanel';
import { formatNarrationDate } from '@/lib/narration/narrationHistory';

function storybuilderStatusLabel(project) {
  const st = String(project?.status || '').toLowerCase();
  if (st === 'completed') return 'Ready';
  if (st === 'running' || st === 'pending') return 'Processing';
  if (st === 'failed') return 'Failed';
  return st || 'Draft';
}

/**
 * Right-side history for Storybuilder create page.
 */
export function StorybuilderHistoryPanel({
  open,
  onToggle,
  activeProjectId = null,
  onSelectProject,
  onNewProject,
  refreshKey = 0,
  loadingProjectId = null,
}) {
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/storybuilder/projects');
      setProjects(Array.isArray(data?.projects) ? data.projects : []);
    } catch {
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load, refreshKey]);

  return (
    <FlowHistoryPanel
      open={open}
      onToggle={onToggle}
      title="Storybuilder history"
      description="Past PDF → narration projects. Open one to resume in the editor or watch progress."
      icon={BookOpen}
      variant="dark"
      loading={loading}
      count={projects.length}
      onRefresh={load}
      refreshLoading={loading}
      onNew={onNewProject}
      newLabel="New Storybuilder"
      emptyMessage="No Storybuilder projects yet. Upload a PDF to create one."
      collapsedLabel="History"
      testId="storybuilder-history-panel"
      footer={`${projects.length} project${projects.length === 1 ? '' : 's'}`}
    >
      <ul className="space-y-1">
        {projects.map((p) => {
          const active = p.id === activeProjectId;
          const status = storybuilderStatusLabel(p);
          return (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => onSelectProject(p)}
                disabled={loadingProjectId === p.id}
                className={cn(
                  'w-full text-left rounded-sm px-2.5 py-2 border transition-colors disabled:opacity-60',
                  active
                    ? 'bg-teal-500/15 border-teal-500/40'
                    : 'border-transparent hover:bg-white/5 hover:border-slate-800',
                )}
                data-testid={`storybuilder-history-item-${p.id}`}
              >
                <p className={cn('text-sm font-medium line-clamp-1', active ? 'text-teal-100' : 'text-slate-200')}>
                  {p.title || 'Untitled'}
                </p>
                {p.pdf_filename ? (
                  <p className="text-[10px] text-slate-500 truncate mt-0.5">{p.pdf_filename}</p>
                ) : null}
                <span className="mt-1 flex items-center justify-between gap-2 text-[10px] uppercase tracking-wide text-slate-500">
                  <span>{status}</span>
                  {p.page_count > 0 ? <span>{p.page_count} pages</span> : null}
                </span>
                <span className="mt-1 flex items-center gap-1 text-[10px] text-slate-500">
                  <Clock className="w-3 h-3" />
                  {formatNarrationDate(p.updated_at || p.created_at)}
                </span>
                {loadingProjectId === p.id ? (
                  <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-teal-300">
                    <Loader2 className="h-3 w-3 animate-spin" /> Loading…
                  </span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
    </FlowHistoryPanel>
  );
}

export default StorybuilderHistoryPanel;
